import { Link } from 'react-router';
import { PersonName } from '../PersonName';
import { isOverdue, type ServerTask } from '../../data/tasks';
import { activityLabel, isUntouchedOverdue } from '../../data/taskActivity';
import type { Store } from '../../data/types';
import { formatDay } from '../dates';
import { DistrictLink, StoreLink } from '../entityLinks';
import { ReopenReason } from './ReopenReason';
import { plural } from '../plural';
import { useOwnStoreOnly } from '../ownStore';
import { equalsIgnoreCase } from '../../data/scope';

/**
 * One task card, shared by the Tasks hub, the store/district detail pages
 * and the task page itself. The title and a "View" button lead to the
 * task's own page (comments/files/history) — the button because a bold
 * title alone didn't read as clickable (owner ask 2026-10-18); store and
 * district names link to theirs.
 */
export function TaskRow({
  task,
  store,
  view,
  canManage,
  busy = false,
  onComplete,
  onReopen,
  showOpen = true,
  noActivity = false,
}: {
  task: ServerTask;
  store: Store | undefined;
  view: 'open' | 'completed';
  canManage: boolean;
  /** The task page's own work in flight (lists complete and reopen in dialogs). */
  busy?: boolean;
  /** Omit to hide the button (the task page completes via the composer). */
  onComplete?: (() => void) | undefined;
  onReopen: () => void;
  /** false on the task's own page (it's already open). */
  showOpen?: boolean;
  /** The task page knows the thread is empty: "Created … — no activity yet". */
  noActivity?: boolean;
}) {
  const overdue = isOverdue(task);
  // A store team's own store on every card: say it once (the page), not on each row (S8).
  const ownStore = useOwnStoreOnly(store);
  const toTheStore = ownStore && !!store && equalsIgnoreCase(task.assignedTo, store.managerEmail);
  return (
    <div className="audit-row task-row">
      <div className="task-main">
        <strong>
          <Link className="entity-link" to={`/tasks/${task.id}`}>
            {task.title}
          </Link>
        </strong>
        {task.status === 'reopened' &&
          (task.reopenNote?.undo ? (
            // A store's own undo (B3) — grey, not the red of a DM's reopen (UI review 3, S1).
            <span className="task-badge task-undone">Completion undone</span>
          ) : (
            <span className="task-badge task-reopened">Reopened</span>
          ))}
        {/* B1 (2026-10-25): completing needs a photo or PDF on the task. */}
        {task.status !== 'complete' && task.proofRequired && (
          <span className="task-badge task-proof" title="A photo or PDF must be added to the task to complete it">
            Proof required
          </span>
        )}
        {/* B2 (2026-10-25): the store asked for more time — a DM/Auditor/Admin answers on the task page. */}
        {task.status !== 'complete' && task.extensionRequest && (
          <span className="task-badge task-more-time" title={`Asked for more time: until ${formatDay(task.extensionRequest.date)}`}>
            More time asked
          </span>
        )}
        {(task.fileCount ?? 0) > 0 && (
          <>
            {/* The space outside the nowrap span, so the line can break before it (UI-9 review: 419 px on a phone). */}
            {' '}
            <span className="task-files" title={`${task.fileCount} file${task.fileCount === 1 ? '' : 's'} attached`}>
              📎 {plural(task.fileCount ?? 0, 'file')}
            </span>
          </>
        )}
        {/* A completion with no proof stands out (exec reflect 2026-10-25). */}
        {/* Only when the count is KNOWN to be 0 — older tasks have no count. */}
        {task.status === 'complete' && task.fileCount === 0 && (
          <span
            className="task-badge task-no-proof"
            title="No photo or file was attached to this task (photos on the audit question don’t count here)"
          >
            No photo or PDF
          </span>
        )}
        <div className="muted task-meta">
          {!ownStore && (
            <>
              {store ? <StoreLink store={store} /> : 'Unknown store'}
              {store && store.district.trim() && (
                <>
                  {' · '}
                  <span className="nowrap">
                    <DistrictLink district={store.district} />
                  </span>
                </>
              )}
              {' · '}
            </>
          )}
          {view === 'open' ? (
            <span className={overdue ? 'error nowrap' : 'nowrap'}>
              due {formatDay(task.dueDate) || '—'}
              {overdue && ' (overdue)'}
            </span>
          ) : (
            <>
              <span className="nowrap">completed {formatDay(task.completedAt) || '—'}</span>
              {task.completedByName && (
                <>
                  {' '}
                  <span className="meta-unit">by {task.completedByName}</span>
                </>
              )}
            </>
          )}
          {!toTheStore && (
            <>
              {' · '}
              <span className="meta-unit">
                assigned to <PersonName upn={task.assignedTo} />
              </span>
            </>
          )}
          {task.auditId !== null && (
            <>
              {' · '}
              <span className="nowrap">from an audit{task.sampleIndex !== null && ` (entry ${task.sampleIndex + 1})`}</span>
            </>
          )}
          {/* "Is anyone working on it?" (owner ruling 2026-10-21) — open work only. */}
          {view === 'open' && (
            <>
              {' · '}
              <span className={overdue && isUntouchedOverdue(task) ? 'error nowrap' : 'nowrap'}>{activityLabel(task, undefined, { noEntries: noActivity })}</span>
            </>
          )}
        </div>
        {/* Why it came back (owner ruling 2026-10-25: on every card, not only in the thread). */}
        {view === 'open' && task.status === 'reopened' && task.reopenNote && (
          <ReopenReason note={task.reopenNote} className="task-reopen" />
        )}
        {task.description && <div className="task-desc">{task.description}</div>}
        {view === 'completed' && task.completionNotes && (
          <div className="task-desc muted">“{task.completionNotes}”</div>
        )}
      </div>
      <div className="actions-inner">
        {/* "View", not "Open" — "Open" is also a task status (owner 2026-11-03); the audit cards say "View" too. */}
        {showOpen && (
          <Link className="btn" to={`/tasks/${task.id}`} aria-label={`View task: ${task.title}`}>
            View
          </Link>
        )}
        {view === 'open' ? (
          onComplete && (
            <button className="btn btn-primary" disabled={busy} onClick={onComplete}>
              Complete
            </button>
          )
        ) : (
          canManage && (
            <button className="btn" disabled={busy} onClick={onReopen}>
              Reopen
            </button>
          )
        )}
      </div>
    </div>
  );
}
